import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import {
  Building2,
  CheckCircle2,
  ArrowRight,
  Plus,
} from 'lucide-react';

interface AccountOrganizationsTabProps {
  onCreateOrgClick: () => void;
}

export const AccountOrganizationsTab: React.FC<AccountOrganizationsTabProps> = ({ onCreateOrgClick }) => {
  const navigate = useNavigate();
  const { organizations, currentOrg, switchOrganization } = useAuth();

  if (organizations.length === 0) {
    return (
      <Card padding="lg" className="text-center space-y-2">
        <Building2 size={28} className="mx-auto text-cf-textMuted" />
        <h4 className="text-xs font-semibold text-cf-textPrimary">No Organizations Found</h4>
        <p className="text-xs text-cf-textSecondary max-w-sm mx-auto">
          You are not currently a member of any organization. Create an organization to manage projects and team members.
        </p>
        <div className="pt-2">
          <Button
            type="button"
            variant="primary"
            size="xs"
            onClick={onCreateOrgClick}
            icon={<Plus size={14} />}
          >
            Create Organization
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {organizations.map((org) => {
        const isCurrent = currentOrg?.id === org.id;
        return (
          <div
            key={org.id}
            onClick={() => {
              if (!isCurrent) {
                switchOrganization(org);
              }
              navigate(`/organization/${org.id}`);
            }}
            className={`p-4 rounded-lg border transition-all cursor-pointer group flex flex-col justify-between ${
              isCurrent
                ? 'bg-cf-surface border-cf-border shadow-xs hover:border-cf-borderActive'
                : 'bg-cf-surface border-cf-border hover:border-cf-borderActive'
            }`}
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded bg-cf-bg border border-cf-border flex items-center justify-center text-cf-textSecondary font-bold text-xs flex-shrink-0 group-hover:text-cf-textPrimary transition-colors">
                    <Building2 size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-cf-textPrimary text-xs flex items-center gap-1.5 truncate">
                      <span className="truncate">{org.name}</span>
                    </div>
                    <span className="text-xs text-cf-textSecondary truncate block">
                      {org.slug}
                    </span>
                  </div>
                </div>

                <span className="text-xs uppercase px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textSecondary font-semibold flex-shrink-0 ml-2">
                  {org.plan_tier}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 my-3 text-center">
                <div className="p-2 rounded bg-cf-bg border border-cf-border/50">
                  <span className="text-xs text-cf-textMuted uppercase block">Role</span>
                  <span className="text-xs font-semibold text-cf-textPrimary uppercase">{org.role || 'owner'}</span>
                </div>
                <div className="p-2 rounded bg-cf-bg border border-cf-border/50">
                  <span className="text-xs text-cf-textMuted uppercase block">Projects</span>
                  <span className="text-xs font-semibold text-cf-textPrimary">{org.project_count || 1}</span>
                </div>
                <div className="p-2 rounded bg-cf-bg border border-cf-border/50">
                  <span className="text-xs text-cf-textMuted uppercase block">Seats</span>
                  <span className="text-xs font-semibold text-cf-textPrimary">{org.member_count || 1}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div>
                {isCurrent && (
                  <span className="text-xs text-cf-textSecondary font-medium flex items-center gap-1.5">
                    <CheckCircle2 size={13} className="text-emerald-400" />
                    <span>Currently Selected</span>
                  </span>
                )}
              </div>

              <span className="inline-flex items-center gap-1 text-xs text-cf-textSecondary group-hover:text-cf-textPrimary transition-colors font-medium ml-auto">
                <span>Open</span>
                <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
